
import { Shield, FileCheck, BarChart3, AlertTriangle, Lock, Scale } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const CmmcAssessment = () => {
  return (
    <LandingPageTemplate
      title="CMMC Assessment"
      description="Comprehensive CMMC readiness assessment to prepare your organization for certification."
      keywords={[
        "CMMC assessment",
        "cybersecurity maturity",
        "DoD compliance",
        "defense contractors",
        "CMMC certification"
      ]}
      canonicalUrl="/cybersecurity/cmmc-assessment/"
      schemaType="service"
      schemaData={{
        name: "CMMC Assessment Services",
        description: "Expert evaluation of your CMMC readiness",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Defense Industry"
        highlighted="CMMC Assessment"
        description="Expert evaluation of your organization's readiness for CMMC certification and compliance."
        ctaText="Schedule Assessment"
        heroPattern="grid"
      />

      <FeatureSection 
        title="Comprehensive CMMC Assessment"
        subtitle="KEY FEATURES"
        description="Our assessment provides a thorough evaluation of your CMMC readiness across all domains."
        features={[
          {
            title: "Maturity Evaluation",
            description: "Assess your cybersecurity maturity level across CMMC domains.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Gap Analysis",
            description: "Identify gaps between current practices and CMMC requirements.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation Review",
            description: "Review of policies, procedures, and practice documentation.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Practice Assessment",
            description: "Evaluation of implemented cybersecurity practices.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Process Maturity",
            description: "Assessment of process maturity across domains.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Review",
            description: "Assessment of access control implementations.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Assessment Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to evaluating your CMMC readiness"
        steps={[
          {
            title: "Initial Scoping",
            description: "Define assessment scope and target CMMC level"
          },
          {
            title: "Practice Review",
            description: "Assess implementation of CMMC practices"
          },
          {
            title: "Process Analysis",
            description: "Evaluate process maturity across domains"
          },
          {
            title: "Gap Assessment",
            description: "Identify gaps in practices and processes"
          },
          {
            title: "Recommendations",
            description: "Develop detailed recommendations for certification"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our CMMC Assessment"
        benefits={[
          {
            title: "CMMC Expertise",
            description: "Work with certified CMMC practitioners"
          },
          {
            title: "Comprehensive Coverage",
            description: "Full evaluation across all CMMC domains"
          },
          {
            title: "Clear Roadmap",
            description: "Detailed path to certification readiness"
          },
          {
            title: "DoD Alignment",
            description: "Ensure alignment with DoD requirements"
          },
          {
            title: "Risk Reduction",
            description: "Identify and address security gaps"
          },
          {
            title: "Certification Preparation",
            description: "Prepare effectively for CMMC certification"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Prepare for CMMC?"
        description="Schedule your CMMC readiness assessment today and start your certification journey."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request CMMC Assessment"
        modalDescription="Tell us about your organization's needs for CMMC certification preparation."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "CMMC Assessment",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default CmmcAssessment;
